import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MeetingSourceStatus } from '@vendasta/meetings';
import { GoogleMeetService } from '../meeting-services/google-meet.service';
import { InstantMeetingSettings, MeetingInfo } from '../meeting-services/interfaces/service-interfaces';
import { DialogEvents, MeetingViewEventType } from './enums/enums';
import { DialogEvent, MeetingEvent } from './interfaces/interfaces';
import { AttendeeInfo } from '../dialogs/attendee-selection-dialog/interfaces/interfaces';
import { AttendeeSelectionDialogComponent } from '../dialogs/attendee-selection-dialog/attendee-selection-dialog.component';
import { InstantMeetingDialogComponent } from '../dialogs/instant-meeting-dialog/instant-meeting-dialog.component';
import { EnableMeetingGenerationInfoDialogComponent } from '../dialogs/enable-meeting-generation-info-dialog/enable-meeting-generation-info-dialog.component';
import { AvailableServices } from '../meeting-services/enums';
import { ZoomMeetingService } from '../meeting-services/zoom-meeting.service';
import { MeetingIntegrationsServiceModule } from '../meeting-integrations-service.module';
import { MicrosoftMeetService } from '../meeting-services/microsoft.service';
import { MeetingIntegrationsStoreService } from '../meeting-integrations.store.service';

@Injectable({ providedIn: MeetingIntegrationsServiceModule })
export class MeetingViewModelService {
  private readonly meetingEvents$$ = new ReplaySubject<MeetingEvent>(1);
  readonly meetingEvents$ = this.meetingEvents$$.asObservable();

  private readonly instantMeetingDialogEvents$$ = new ReplaySubject<DialogEvent>(1);
  readonly instantMeetingDialogEvents$ = this.instantMeetingDialogEvents$$.asObservable();

  private readonly attendeeSelectionDialog$$ = new ReplaySubject<AttendeeSelectionDialogComponent>(1);
  private readonly instantMeetingDialog$$ = new BehaviorSubject<InstantMeetingDialogComponent>(null);
  private readonly meetingSettingInfoDialog$$ = new BehaviorSubject<EnableMeetingGenerationInfoDialogComponent>(null);

  constructor(
    private readonly zoomMeetingService: ZoomMeetingService,
    private readonly googleMeetingService: GoogleMeetService,
    private readonly microsoftMeetingService: MicrosoftMeetService,
    private readonly meetingIntegrationsStoreService: MeetingIntegrationsStoreService,
  ) {}

  /**
   * Re-triggers the List API to fetch fresh connect/disconnect URLs with valid auth tokens.
   * Call this when navigating to a page where connect/disconnect actions are performed
   * (e.g., Settings page) to avoid stale tokens from the initial page load.
   * @param filterKeys Optional array of redirect keys to load. If omitted, all keys are loaded.
   */
  public refreshSources(filterKeys?: string[]): void {
    this.meetingIntegrationsStoreService.loadSources(filterKeys);
  }

  public emitEvent(event: MeetingViewEventType, service: AvailableServices, trackedFields?: any): void {
    const newEvent: MeetingEvent = {
      type: event,
      service: service,
      trackingFields: trackedFields,
    };
    this.meetingEvents$$.next(newEvent);
  }

  public get instantMeetingDialogRef(): InstantMeetingDialogComponent {
    return this.instantMeetingDialog$$.getValue();
  }

  public get meetingEvents(): Observable<MeetingEvent> {
    return this.meetingEvents$;
  }

  public get instantMeetingDialogEvents(): Observable<DialogEvent> {
    return this.instantMeetingDialogEvents$;
  }

  public get meetingSettingInfoDialogRef(): EnableMeetingGenerationInfoDialogComponent {
    return this.meetingSettingInfoDialog$$.getValue();
  }

  public isServiceConnected$(source: AvailableServices): Observable<boolean> {
    switch (source) {
      case AvailableServices.ZOOM:
        return this.zoomMeetingService
          .connectionStatus$()
          .pipe(map((status) => status === MeetingSourceStatus.MEETING_SOURCE_STATUS_CONNECTED));
      case AvailableServices.GOOGLE_MEET:
        return this.googleMeetingService
          .connectionStatus$()
          .pipe(map((status) => status === MeetingSourceStatus.MEETING_SOURCE_STATUS_CONNECTED));
      case AvailableServices.MICROSOFT:
        return this.microsoftMeetingService
          .connectionStatus$()
          .pipe(map((status) => status === MeetingSourceStatus.MEETING_SOURCE_STATUS_CONNECTED));
      default:
        console.error(`Cannot find service to check connection status for source: ${source}.`);
    }
  }

  /**
   * toggleConnection() will open a window to authorize or unauthorize an account.
   * Which one the user is given is dependent on their current connection status with the source requested.
   * If they are connected to the source then it will offer an unauthorize url, and vice-versa.
   * @param source the meeting source whose authorization with the requester will change
   * @param connectionInfoKey
   */
  public toggleConnection(source: AvailableServices, connectionInfoKey?: string): void {
    switch (source) {
      case AvailableServices.ZOOM:
        this.zoomMeetingService.toggleConnection(connectionInfoKey);
        break;
      case AvailableServices.GOOGLE_MEET:
        this.googleMeetingService.toggleConnection(connectionInfoKey);
        break;
      case AvailableServices.MICROSOFT:
        this.microsoftMeetingService.toggleConnection(connectionInfoKey);
        break;
      default:
        console.error(`Cannot find service to toggle connection for source ${source}`);
    }
  }

  public initializeInstantMeeting(service: AvailableServices, settings: InstantMeetingSettings): void {
    switch (service) {
      case AvailableServices.ZOOM:
        this.zoomMeetingService.createInstantMeeting(settings);
        break;
      case AvailableServices.GOOGLE_MEET:
        this.googleMeetingService.createInstantMeeting(settings);
    }
  }

  public instantMeetingInfo(service: AvailableServices): Observable<MeetingInfo> {
    switch (service) {
      case AvailableServices.ZOOM:
        return this.zoomMeetingService.meetingInfo();
      case AvailableServices.GOOGLE_MEET:
        return this.googleMeetingService.meetingInfo();
    }
  }

  registerAttendeeSelectionDialog(dialog: AttendeeSelectionDialogComponent): void {
    this.attendeeSelectionDialog$$.next(dialog);
  }

  // TODO: find a more elegant way of handling registrations
  unregisterAttendeeSelectionDialog(): void {
    this.attendeeSelectionDialog$$.next(null);
  }

  registerInstantMeetingDialog(dialog: InstantMeetingDialogComponent): void {
    this.instantMeetingDialog$$.next(dialog);
  }

  unregisterInstantMeetingDialog(): void {
    this.instantMeetingDialog$$.next(null);
  }

  registerMeetingSettingInfoDialog(dialog: EnableMeetingGenerationInfoDialogComponent): void {
    this.meetingSettingInfoDialog$$.next(dialog);
  }

  unregisterEnableMeetingGenerationInfoDialog(): void {
    this.meetingSettingInfoDialog$$.next(null);
  }

  public async updateInstantMeetingAttendees(attendees: AttendeeInfo[]): Promise<void> {
    const dialog = await this.attendeeSelectionDialog$$.pipe(take(1)).toPromise();
    if (dialog) {
      dialog.updateAttendees(attendees);
    }
  }

  public emitAddAttendeeRequest(addAttendeeParams: any): void {
    this.instantMeetingDialogEvents$$.next(<DialogEvent>{
      type: DialogEvents.ADD_ATTENDEE,
      options: addAttendeeParams,
    });
  }
}
